/** Home in the new design (boards: docs/design/canvas/project/Home*.dc.html; decisions:
 *  docs/design/inventory/home-notes.md and home-build-notes.md). Shown only to people on the new design;
 *  everyone else keeps the classic Overview.
 *
 *  Correctness first (owner, 4 Nov): every figure comes from the API the classic pages already use.
 *  - Sales and cases per period: /sales/years (the gross sales workbook; the same figures as Sales › Years).
 *  - Classic Overview figures (sales report total, finished stock, in transit, issues, drafts, channels,
 *    top customers, errors, recent documents): the same calls the classic Overview makes.
 *  - Running low: /stock/cover (non-supplier rows on alert or warn) and BCI's raw-material shortages
 *    (open data issues of check BCI_MATERIAL_SHORT).
 *  - Money only for people with books access; nobody else calls /quickbooks. */
import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowDownRight, ArrowRight, ArrowRightLeft, ArrowUp, ArrowUpRight, ChartColumn, ChevronRight, CircleAlert, CircleCheck,
  CircleX, ClipboardList, ClockAlert, FilePenLine, FileText, HandCoins, Info, ListChecks, LoaderCircle, OctagonAlert,
  Package, PackageCheck, RotateCcw, Scale, Send, Table, TrendingUp, TriangleAlert, Truck, Users, X, type LucideIcon,
} from "lucide-react";
// Every icon is the one docs/design/tokens.json names for that thing (nav-sales, transfer, po, late, ...).

import { useAuth } from "../../components/Auth";
import { api, ApiError, type DocStatus, type DocSummary, type Issue } from "../../lib/api";
import { day, num, pct, usd, when } from "../../lib/format";
import { useApi } from "../../lib/useApi";
import { describe, type Form, type Item } from "../DocumentForm";
import "./Home.css";

// ───────────────────────────── API shapes ─────────────────────────────
interface Overview {
  sales: { amount_usd: number; cases: number; as_of: string | null; reconciles: boolean | null;
           by_channel: { code: string; name: string; amount_usd: number; cases: number }[] };
  stock: { finished_cases: number; locations: number };
  in_transit: { cases: number; documents: number };
  documents: Record<string, number>;
  issues: { error: number; warning: number; info: number; total: number };
  tasks_open?: number;
}
interface YearMonth { year: number; month: number; usd: number; last_usd: number | null; cases: number; last_cases: number | null }
interface Years {
  available: boolean; year: number; last_year: number; as_of: string | null; file: string;
  months_reported: number; partial_month: boolean;
  ytd: { usd: number; last_usd: number; change: number | null; cases: number; last_cases: number; cases_change: number | null };
  ttm: { usd: number; previous_usd: number | null; change: number | null; cases: number; previous_cases: number | null;
         cases_change: number | null; from: string; to: string; months: YearMonth[] };
  this_month: { year: number; month: number; partial: boolean; days: number | null; days_in_month: number;
                usd: number; last_usd: number; change: number | null; cases: number; last_cases: number; cases_change: number | null };
  monthly: { month: number; usd: number | null; last_usd: number; cases: number | null; last_cases: number }[];
}
interface CoverRow {
  sku: string; product: string; location: string; location_name: string; supplier: boolean;
  status: "alert" | "warn" | "ok" | "no_movement" | "no_history"; cases: number; weekly: number | null;
  weeks_left: number | null; as_of: string | null; readings: number; stale: boolean;
}
interface Cover { rows: CoverRow[]; basis: { window_days: number; min_readings: number; min_span_days: number; warn_weeks: number; alert_weeks: number; stale_days: number } }
interface Monthly { undated: { count: number; amount_usd: number } }
interface Cust { id: number; name: string; amount_usd: number; share: number }
interface Freshness { scheduled: number; quiet: { code: string; name: string; summary: string }[] }
interface Books {
  configured: boolean; connected: boolean; as_of: string | null;
  open_invoices: { doc_number: string | null; customer: string | null; due_date: string | null; currency: string;
                   balance: number | null; days_late: number | null }[];
  aging?: { bucket: string; balance: number }[]; open_total?: number;
  invoice_check?: { counts: Record<string, number> };
}
interface Tasks { tasks: { id: number; title: string; done: boolean }[] }
interface DocDefaults extends Record<string, unknown> { number: string; to_location: string | null; ship_to_id: number | null }
interface Loc { code: string; name: string }
interface Party { id: number; name: string }

type Period = "month" | "ytd" | "l12";
const PERIODS: { id: Period; label: string }[] = [
  { id: "month", label: "This month" }, { id: "ytd", label: "Year to date" }, { id: "l12", label: "Last 12 months" },
];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const ROWS_SHOWN = 8;

// ───────────────────────────── small pieces ─────────────────────────────
function Ic({ i: I, s = 18, cls }: { i: LucideIcon; s?: number; cls?: string }) {
  return <I size={s} className={cls} aria-hidden="true" />;
}

/** "+12.3%" / "−6.0%", with an arrow and a word-free tone that never stands alone (the sign is the word). */
function signed(change: number, dp = 1) {
  return `${change >= 0 ? "+" : "−"}${Math.abs(change * 100).toFixed(dp)}%`;
}
function Delta({ change }: { change: number | null }) {
  if (change == null) return null;
  const flat = Math.abs(change) < 0.01;
  const up = change > 0;
  const I = flat ? ArrowRight : up ? ArrowUpRight : ArrowDownRight;
  return <span className={`nh-delta ${flat ? "tx-neu" : up ? "tx-good" : "tx-crit"}`} data-testid="delta"><Ic i={I} s={13} />{signed(change)}</span>;
}

function Card({ area, icon, title, right, isNew, children, testId }: {
  area: string; icon: LucideIcon; title: ReactNode; right?: ReactNode; isNew?: boolean; children: ReactNode; testId?: string;
}) {
  return (
    <section className={`nh-card nh-cq nh-a-${area}`} aria-label={typeof title === "string" ? title : undefined} data-testid={testId}>
      <div className="nh-card-h">
        <h2><Ic i={icon} />{title}{isNew ? <span className="nh-new">NEW</span> : null}</h2>
        {right ? <span className="r">{right}</span> : null}
      </div>
      {children}
    </section>
  );
}

function Skel({ n = 4 }: { n?: number }) {
  const w = [92, 70, 84, 60, 78, 66, 88, 74];
  return <div className="nh-skrows" aria-busy="true" aria-label="Loading">{Array.from({ length: n }, (_, i) => <span key={i} className="nh-sk" style={{ width: `${w[i % w.length]}%` }} />)}</div>;
}

function Failed({ what, error, retry }: { what: string; error: string; retry: () => void }) {
  return (
    <div className="nh-err" role="alert">
      <Ic i={CircleX} />
      <div>
        <b>Could not load {what}</b>
        <p>{error}. Nothing was changed; the rest of the page is fine.</p>
        <button type="button" className="nh-btn sec sm" onClick={retry}><Ic i={RotateCcw} s={15} />Try again</button>
      </div>
    </div>
  );
}

function Clear({ icon = CircleCheck, good = true, title, children }: { icon?: LucideIcon; good?: boolean; title: string; children: ReactNode }) {
  return (
    <div className="nh-clear">
      <span className={`nh-circ${good ? " ico-good" : ""}`}><Ic i={icon} s={22} /></span>
      <div><b>{title}</b><p>{children}</p></div>
    </div>
  );
}

const DOC_STATUS: Record<DocStatus, { tone: string; icon: LucideIcon; word: string }> = {
  draft: { tone: "neu", icon: FilePenLine, word: "Draft" }, issued: { tone: "info", icon: Send, word: "Issued" },
  shipped: { tone: "warn", icon: Truck, word: "Shipped" }, received: { tone: "good", icon: PackageCheck, word: "Received" },
  cancelled: { tone: "neu", icon: X, word: "Cancelled" },
};
function DocTag({ d }: { d: DocSummary }) {
  const s = DOC_STATUS[d.status];
  const word = d.status === "shipped" && d.doc_type === "transfer_invoice" ? "In transit" : s.word;
  return <span className={`nh-tag ${s.tone}`} data-status={d.status}><Ic i={s.icon} s={13} />{word}</span>;
}

const dayShort = (iso: string | null | undefined) => (iso ? day(iso).replace(/ \d{4}$/, "") : "—");

// ───────────────────────────── the page ─────────────────────────────
export function Home() {
  const { user } = useAuth();
  const books = Boolean(user?.sees_books);
  const [params, setParams] = useSearchParams();
  const period: Period = (["month", "ytd", "l12"] as const).find((p) => p === params.get("period")) ?? "ytd";
  const setPeriod = (p: Period) => setParams((q) => { const n = new URLSearchParams(q); if (p === "ytd") n.delete("period"); else n.set("period", p); return n; }, { replace: true });

  const o = useApi<Overview>("/overview");
  const years = useApi<Years>("/sales/years");
  const cover = useApi<Cover>("/stock/cover");
  const short = useApi<Issue[]>("/issues?check=BCI_MATERIAL_SHORT");
  const errors = useApi<Issue[]>("/issues?severity=error");
  const docs = useApi<DocSummary[]>("/documents");
  const customers = useApi<{ customers: Cust[] }>("/sales/by-customer?limit=8");
  const monthly = useApi<Monthly>("/sales/monthly");
  const fresh = useApi<Freshness>("/freshness");
  // Money only for people with books access: nobody else even asks for it.
  const qb = useApi<Books>(books ? "/quickbooks" : null);
  const tasks = useApi<Tasks>(o.data?.tasks_open ? "/tasks" : null);

  const [dialog, setDialog] = useState<{ kind: "transfer"; row: CoverRow } | { kind: "po"; row: Issue } | null>(null);
  const [toast, setToast] = useState<Saved | null>(null);
  const [drafted, setDrafted] = useState<Record<string, string>>({});

  const y = years.data && years.data.available ? years.data : null;
  const subtitle = y ? periodText(y, period) : "All channels";
  const onSaved = (s: Saved) => { setDialog(null); setToast(s); setDrafted((d) => ({ ...d, [s.rowKey]: s.note })); };

  return (
    <div className={`nh ${books ? "books" : "plain"}`} data-testid="new-home">
      <div className="nh-hh">
        <div>
          <h1>Home</h1>
          <p className="nh-sub" data-testid="home-sub">{subtitle}</p>
        </div>
        <div className="nh-seg" role="radiogroup" aria-label="Period">
          {PERIODS.map((p) => (
            <button key={p.id} type="button" role="radio" aria-checked={period === p.id} onClick={() => setPeriod(p.id)}>{p.label}</button>
          ))}
        </div>
      </div>

      <Headline period={period} years={years} o={o} qb={books ? qb : null} />

      <div className="nh-grid">
        <RunningLow cover={cover} short={short} drafted={drafted}
                    onTransfer={(row) => setDialog({ kind: "transfer", row })} onPO={(row) => setDialog({ kind: "po", row })} />
        <NeedsALook o={o} errors={errors} fresh={fresh} qb={books ? qb : null} tasks={tasks} />
        <SalesChart period={period} years={years} />
        {books ? <Owed qb={qb} admin={user?.role === "admin"} /> : null}
        <TopCustomers customers={customers} />
        <Channels o={o} monthly={monthly} years={y} />
        <RecentDocs docs={docs} />
      </div>

      {dialog?.kind === "transfer" && cover.data ? (
        <TransferDialog row={dialog.row} rows={cover.data.rows} warn={cover.data.basis.warn_weeks} onClose={() => setDialog(null)} onSaved={onSaved} />
      ) : null}
      {dialog?.kind === "po" ? <PODialog issue={dialog.row} onClose={() => setDialog(null)} onSaved={onSaved} /> : null}
      {toast ? <Toast saved={toast} onClose={() => setToast(null)} onUndone={(key) => setDrafted((d) => { const n = { ...d }; delete n[key]; return n; })} /> : null}
    </div>
  );
}

/** The line under the title: which months, against which, and how old the figures are. */
function periodText(y: Years, period: Period): string {
  const lastMon = MONTH[y.months_reported - 1];
  const partial = y.partial_month ? ` ${lastMon} ${y.year} is not over yet and is compared with all of ${lastMon} ${y.last_year}.` : "";
  const src = ` Gross sales workbook as of ${day(y.as_of)}.`;
  if (period === "month") {
    const m = y.this_month;
    return `All channels · ${MONTH[m.month - 1]} ${m.year}${m.partial ? ` so far (${m.days} of ${m.days_in_month} days)` : ""}, beside ${MONTH[m.month - 1]} ${y.last_year}.${src}`;
  }
  if (period === "l12") {
    const t = y.ttm; const a = t.months[0]; const b = t.months[11];
    return `All channels · last 12 months, ${MON[a.month - 1]} ${a.year} – ${MON[b.month - 1]} ${b.year}, compared with ${MON[a.month - 1]} ${a.year - 1} – ${MON[b.month - 1]} ${b.year - 1}.${partial}${src}`;
  }
  return `All channels · year to date, Jan–${MON[y.months_reported - 1]} ${y.year}, compared with the same months of ${y.last_year}.${partial}${src}`;
}

// ───────────────────────────── headline tiles ─────────────────────────────
type Loaded<T> = { data: T | null; error: string | null; reload: () => void };

function Headline({ period, years, o, qb }: { period: Period; years: Loaded<Years>; o: Loaded<Overview>; qb: Loaded<Books> | null }) {
  const y = years.data;
  const tiles: ReactNode[] = [];
  const skel = (label: string, icon: LucideIcon) => (
    <div className="nh-card" aria-busy="true" key={label}><div className="nh-eb"><Ic i={icon} s={13} />{label}</div>
      <span className="nh-sk" style={{ width: "60%", height: 26, margin: "12px 0 8px" }} /><span className="nh-sk" style={{ width: "80%" }} /></div>
  );
  if (years.error) {
    tiles.push(<div className="nh-card wide" key="ye"><Failed what="sales and cases" error={years.error} retry={years.reload} /></div>);
  } else if (!y) {
    tiles.push(skel("Sales", TrendingUp), skel("Cases sold", Package));
  } else if (!y.available) {
    tiles.push(
      <div className="nh-card wide" key="na"><Clear icon={TrendingUp} good={false} title="No sales workbook yet">
        Sales and cases against last year appear once Zach's gross sales workbook has been read (Sources).</Clear></div>,
    );
  } else {
    const f = figures(y, period);
    tiles.push(
      <Link to="/sales?tab=years" className="nh-card nh-tile" key="sales" data-testid="tile-sales">
        <div className="nh-eb"><Ic i={TrendingUp} s={13} />Sales</div>
        <div className="nh-big" data-testid="value">{usd(f.usd)}</div>
        <Versus change={f.change} last={f.last_usd == null ? null : usd(f.last_usd)} partial={f.partial} lastYear={y.last_year} />
        <div className="nh-line2">{f.line}</div>
      </Link>,
      <Link to="/sales?tab=years" className="nh-card nh-tile" key="cases" data-testid="tile-cases">
        <div className="nh-eb"><Ic i={Package} s={13} />Cases sold</div>
        <div className="nh-big" data-testid="value">{num(f.cases)}</div>
        <Versus change={f.cases_change} last={f.last_cases == null ? null : num(f.last_cases)} partial={f.partial} lastYear={y.last_year} />
        <div className="nh-line2">{f.line}</div>
      </Link>,
    );
  }
  if (qb) tiles.push(<OwedTile key="owed" qb={qb} />);
  if (o.error) tiles.push(<div className="nh-card" key="se"><Failed what="finished stock" error={o.error} retry={o.reload} /></div>);
  else if (!o.data) tiles.push(skel("Finished stock", Package));
  else {
    const d = o.data;
    tiles.push(
      <div className="nh-card" key="stock" data-testid="tile-stock">
        <div className="nh-eb"><Ic i={Package} s={13} />Finished stock</div>
        <div className="nh-big"><Link to="/stock" data-testid="value">{num(d.stock.finished_cases)}</Link></div>
        <div className="nh-vs" data-testid="sub">cases across {d.stock.locations} locations</div>
        <div className="nh-line2">today · <Link to="/stock?tab=transit" data-testid="transit">{num(d.in_transit.cases)} cases in transit on {d.in_transit.documents} transfer(s)</Link></div>
      </div>,
    );
  }
  return <section className="nh-kp" aria-label="Headline figures">{tiles}</section>;
}

function Versus({ change, last, partial, lastYear }: { change: number | null; last: string | null; partial: boolean; lastYear: number }) {
  if (last == null) return <span className="nh-vs"><Ic i={Info} s={13} /> No {lastYear} figures to compare with</span>;
  if (partial) return <span className="nh-vs">vs {last} in the whole month of {lastYear}</span>;
  if (change == null) return <span className="nh-vs"><Ic i={Info} s={13} /> No {lastYear} sales to compare with</span>;
  return <><Delta change={change} /> <span className="nh-vs">vs {last}</span></>;
}

/** The figures for one period, straight from /sales/years. */
function figures(y: Years, period: Period) {
  if (period === "month") {
    const m = y.this_month;
    return { usd: m.usd, last_usd: m.last_usd, change: m.change, cases: m.cases, last_cases: m.last_cases, cases_change: m.cases_change,
             partial: m.partial, line: m.partial ? `${MONTH[m.month - 1]} so far: ${m.days} of ${m.days_in_month} days, so no change is worked out`
               : `${MONTH[m.month - 1]} ${m.year} · vs ${MONTH[m.month - 1]} ${y.last_year}` };
  }
  if (period === "l12") {
    const t = y.ttm;
    return { usd: t.usd, last_usd: t.previous_usd, change: t.change, cases: t.cases, last_cases: t.previous_cases,
             cases_change: t.cases_change, partial: false, line: "last 12 months · vs the 12 before" };
  }
  const t = y.ytd;
  return { usd: t.usd, last_usd: t.last_usd, change: t.change, cases: t.cases, last_cases: t.last_cases, cases_change: t.cases_change,
           partial: false, line: `year to date · vs Jan–${MON[y.months_reported - 1]} ${y.last_year}` };
}

function OwedTile({ qb }: { qb: Loaded<Books> }) {
  const d = qb.data;
  const head = <div className="nh-eb"><Ic i={HandCoins} s={13} />Owed to us</div>;
  if (qb.error) return <div className="nh-card"><Failed what="money owed" error={qb.error} retry={qb.reload} /></div>;
  if (!d) return <div className="nh-card" aria-busy="true">{head}<span className="nh-sk" style={{ width: "60%", height: 26, margin: "12px 0 8px" }} /></div>;
  if (!d.connected || !d.as_of) {
    return <Link to="/quickbooks" className="nh-card nh-tile" data-testid="tile-owed">{head}<div className="nh-big">—</div>
      <div className="nh-line2">{d.connected ? "QuickBooks is connected; its first read has not run yet" : "QuickBooks is not connected yet"}</div></Link>;
  }
  const over90 = d.aging?.find((a) => a.bucket === "Over 90 days")?.balance ?? 0;
  return (
    <Link to="/quickbooks" className="nh-card nh-tile" data-testid="tile-owed">
      {head}
      <div className="nh-big">{usd(d.open_total ?? 0)}</div>
      {over90 > 0 ? <span className="nh-tag crit"><Ic i={OctagonAlert} s={13} />{usd(over90)} over 90 days</span>
        : <span className="nh-tag good"><Ic i={CircleCheck} s={13} />Nothing over 90 days</span>}
      <div className="nh-line2">today, from QuickBooks</div>
    </Link>
  );
}

// ───────────────────────────── running low ─────────────────────────────
interface Low { key: string; kind: "product" | "raw"; row?: CoverRow; issue?: Issue }

function RunningLow({ cover, short, drafted, onTransfer, onPO }: {
  cover: Loaded<Cover>; short: Loaded<Issue[]>; drafted: Record<string, string>;
  onTransfer: (r: CoverRow) => void; onPO: (i: Issue) => void;
}) {
  const [all, setAll] = useState(false);
  const right = <Link to="/stock">All stock →</Link>;
  if (cover.error) return <Card area="low" icon={CircleAlert} title="Running low" right={right} isNew><Failed what="running low" error={cover.error} retry={cover.reload} /></Card>;
  if (!cover.data || (!short.data && !short.error)) return <Card area="low" icon={CircleAlert} title="Running low" right={right} isNew><Skel n={6} /></Card>;
  const b = cover.data.basis;
  const products = cover.data.rows.filter((r) => !r.supplier && (r.status === "alert" || r.status === "warn"));
  const raws = short.data ?? [];
  // Raw materials first (the production plan needs them this month or next), then products, fewest weeks first
  // (the API's order).
  const list: Low[] = [
    ...raws.map((i) => ({ key: `raw:${i.id}`, kind: "raw" as const, issue: i })),
    ...products.map((r) => ({ key: `t:${r.location}:${r.sku}`, kind: "product" as const, row: r })),
  ];
  const shown = all ? list : list.slice(0, ROWS_SHOWN);
  const alerts = products.filter((r) => r.status === "alert").length;

  return (
    <Card area="low" icon={CircleAlert} title="Running low" right={right} isNew testId="running-low">
      {short.error ? <Failed what="raw-material shortages" error={short.error} retry={short.reload} /> : null}
      {list.length === 0 ? (
        <Clear title="Nothing is running low">
          Every product with enough history has {num(b.warn_weeks)} weeks or more left (the warn level). No raw material is short in BCI's production plan.
        </Clear>
      ) : (
        <>
          <div className="nh-tw nh-lowt">
            <table className="nh-t" data-testid="low-table">
              <colgroup><col style={{ width: "34%" }} /><col style={{ width: "13%" }} /><col style={{ width: "16%" }} /><col style={{ width: "19%" }} /><col style={{ width: "18%" }} /></colgroup>
              <thead><tr>
                <th>Product · warehouse</th><th className="r">On hand</th><th className="r">Out / week</th>
                <th className="r sorted" aria-sort="ascending"><span className="sb"><Ic i={ArrowUp} s={13} />Weeks left</span></th>
                <th className="r"><span className="sr">Actions</span></th>
              </tr></thead>
              <tbody>{shown.map((l) => <LowRow key={l.key} l={l} note={drafted[l.key]} onTransfer={onTransfer} onPO={onPO} />)}</tbody>
            </table>
          </div>
          <div className="nh-lowl" data-testid="low-list">
            {shown.map((l) => <LowItem key={l.key} l={l} note={drafted[l.key]} onTransfer={onTransfer} onPO={onPO} />)}
          </div>
          {list.length > ROWS_SHOWN ? (
            <button type="button" className="nh-btn qt sm" style={{ marginTop: 6 }} onClick={() => setAll((a) => !a)} aria-expanded={all}>
              {all ? `Show the first ${ROWS_SHOWN}` : `Show all ${list.length}`}
            </button>
          ) : null}
        </>
      )}
      <HistoryBand rows={cover.data.rows} />
      <p className="nh-basis">
        Weeks left = cases on hand ÷ average weekly outflow. Outflow comes from the warehouses' daily stock readings: a fall
        between two readings is goods out; a rise is a delivery and is ignored. Based on the last {b.window_days} days of
        readings (at least {b.min_readings} readings over {b.min_span_days} days). Warn under {num(b.warn_weeks)} weeks,
        alert under {num(b.alert_weeks)}. The bottler and distillery are left out: their stock going out is shipping.
        Raw materials short come from BCI's production plan.
      </p>
      <div className="nh-foot" data-testid="low-foot">
        <span>{products.length} product{products.length === 1 ? "" : "s"} under {num(b.warn_weeks)} weeks ({alerts} under {num(b.alert_weeks)})
          {" "}+ {raws.length} raw material{raws.length === 1 ? "" : "s"} short at BCI · raw materials first, then fewest weeks left</span>
      </div>
    </Card>
  );
}

/** One BCI shortage. Issues raised before 4 Nov carry the name and month only in their sentence
 *  ("BCI short 182 Gal of <name> after the 576 they hold — first needed in Nov"), so read it from there. */
function rawFacts(i: Issue) {
  const d = i.detail as Record<string, string | undefined>;
  const name = d.name || /\bof (.+?) after the /.exec(i.summary)?.[1] || String(d.id ?? "");
  const needed = d.first_needed || /first needed in (\w+)/.exec(i.summary)?.[1] || null;
  return { sku: String(d.id ?? ""), name, uom: d.uom ?? "", short: Number(d.shortfall ?? 0), have: Number(d.on_hand ?? 0), needed };
}

function LeftTag({ l }: { l: Low }) {
  if (l.kind === "raw") {
    const f = rawFacts(l.issue!);
    return <span className="nh-woc"><span className="nh-tag crit"><Ic i={OctagonAlert} s={13} />{f.needed ? `Needed in ${f.needed}` : "Short"}</span>
      <span className="mut">{num(f.short)} {f.uom} short</span></span>;
  }
  const r = l.row!;
  const crit = r.status === "alert";
  return <span className="nh-woc"><span className={`nh-tag ${crit ? "crit" : "warn"}`} data-testid="weeks"><Ic i={crit ? OctagonAlert : ClockAlert} s={13} />{r.weeks_left == null ? "—" : r.weeks_left.toFixed(1)} wk</span></span>;
}

function LowRow({ l, note, onTransfer, onPO }: { l: Low; note?: string; onTransfer: (r: CoverRow) => void; onPO: (i: Issue) => void }) {
  if (l.kind === "raw") {
    const f = rawFacts(l.issue!);
    return (
      <tr data-testid="low-row" data-kind="raw" data-sku={f.sku}>
        <td className="two"><Link className="trunc" to={`/products/${encodeURIComponent(f.sku)}`} title={f.name}>{f.name}</Link>
          <span className="sub">At BCI · raw material · <span className="mono">{f.sku}</span></span>{note ? <span className="nh-drafted"><Ic i={FilePenLine} s={13} /> {note}</span> : null}</td>
        <td className="r two">{num(f.have)}<span className="sub">{f.uom}</span></td>
        <td className="r">—</td>
        <td className="r tg"><LeftTag l={l} /></td>
        <td className="r tg"><button type="button" className="nh-btn sec sm" onClick={() => onPO(l.issue!)} aria-label={`Raise a purchase order for ${f.name}`}><Ic i={ClipboardList} s={15} />PO</button></td>
      </tr>
    );
  }
  const r = l.row!;
  return (
    <tr data-testid="low-row" data-kind="product" data-sku={r.sku} data-location={r.location}>
      <td className="two"><Link className="trunc" to={`/products/${encodeURIComponent(r.sku)}`} title={r.product}>{r.product}</Link>
        <span className="sub" title={r.location_name}>{r.location_name} · <span className="mono">{r.sku}</span>{r.stale ? ` · last reading ${dayShort(r.as_of)}` : ""}</span>
        {note ? <span className="nh-drafted"><Ic i={FilePenLine} s={13} /> {note}</span> : null}</td>
      <td className="r">{num(r.cases)}</td>
      <td className="r">{num(r.weekly, 1)}</td>
      <td className="r tg"><LeftTag l={l} /></td>
      <td className="r tg"><button type="button" className="nh-btn sec sm" onClick={() => onTransfer(r)} aria-label={`Make a transfer invoice for ${r.product} to ${r.location_name}`}><Ic i={ArrowRightLeft} s={15} />Transfer</button></td>
    </tr>
  );
}

function LowItem({ l, note, onTransfer, onPO }: { l: Low; note?: string; onTransfer: (r: CoverRow) => void; onPO: (i: Issue) => void }) {
  if (l.kind === "raw") {
    const f = rawFacts(l.issue!);
    return (
      <div className="nh-lr" data-kind="raw">
        <div className="t"><Link to={`/products/${encodeURIComponent(f.sku)}`}>{f.name}</Link>
          <span>At BCI · {num(f.have)} {f.uom} on hand · {num(f.short)} {f.uom} short</span>{note ? <span className="nh-drafted">{note}</span> : null}</div>
        <span className="nh-tag crit"><Ic i={OctagonAlert} s={13} />{f.needed ? `Needed in ${f.needed}` : "Short"}</span>
        <button type="button" className="nh-btn sec" onClick={() => onPO(l.issue!)} aria-label={`Raise a purchase order for ${f.name}`}><Ic i={ClipboardList} s={15} />PO</button>
      </div>
    );
  }
  const r = l.row!;
  const crit = r.status === "alert";
  return (
    <div className="nh-lr" data-kind="product">
      <div className="t"><Link to={`/products/${encodeURIComponent(r.sku)}`}>{r.product}</Link>
        <span>{r.location_name} · {num(r.cases)} cases · {num(r.weekly, 1)} out a week{r.stale ? ` · last reading ${dayShort(r.as_of)}` : ""}</span>
        {note ? <span className="nh-drafted">{note}</span> : null}</div>
      <span className={`nh-tag ${crit ? "crit" : "warn"}`}><Ic i={crit ? OctagonAlert : ClockAlert} s={13} />{r.weeks_left == null ? "—" : r.weeks_left.toFixed(1)} wk</span>
      <button type="button" className="nh-btn sec" onClick={() => onTransfer(r)} aria-label={`Make a transfer invoice for ${r.product} to ${r.location_name}`}><Ic i={ArrowRightLeft} s={15} />Transfer</button>
    </div>
  );
}

/** Warehouses whose readings are too few for a pace: named with their reading count, no number guessed. */
function HistoryBand({ rows }: { rows: CoverRow[] }) {
  const by = new Map<string, { name: string; readings: number; last: string | null; stale: boolean }>();
  for (const r of rows) {
    if (r.supplier || r.status !== "no_history") continue;
    const cur = by.get(r.location);
    if (!cur || r.readings > cur.readings) by.set(r.location, { name: r.location_name, readings: r.readings, last: r.as_of, stale: r.stale });
  }
  if (!by.size) return null;
  const list = [...by.values()].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="nh-hist" data-testid="history-band">
      <Ic i={Info} s={15} />
      <div><b>Not enough history yet</b> for weeks left in {list.length} warehouse{list.length === 1 ? "" : "s"}. Weeks left shows once a
        warehouse has enough daily readings; no number is guessed before then.
        <ul>{list.map((w) => (
          <li key={w.name} data-testid="history-item">{w.name}: {w.readings} reading{w.readings === 1 ? "" : "s"}{w.stale ? `, last ${dayShort(w.last)}` : ""}</li>
        ))}</ul>
      </div>
    </div>
  );
}

// ───────────────────────────── needs a look ─────────────────────────────
function NeedsALook({ o, errors, fresh, qb, tasks }: {
  o: Loaded<Overview>; errors: Loaded<Issue[]>; fresh: Loaded<Freshness>; qb: Loaded<Books> | null; tasks: Loaded<Tasks>;
}) {
  if (o.error) return <Card area="look" icon={ListChecks} title="Needs a look" isNew><Failed what="what needs a look" error={o.error} retry={o.reload} /></Card>;
  if (!o.data) return <Card area="look" icon={ListChecks} title="Needs a look" isNew><Skel n={5} /></Card>;
  const d = o.data;
  const rows: ReactNode[] = [];
  const row = (key: string, to: string, tone: string, icon: LucideIcon, n: ReactNode, title: ReactNode, sub: ReactNode, extra?: ReactNode) => {
    rows.push(
      <div key={key} data-testid={`look-${key}`}>
        <Link className="nh-lk" to={to}>
          <span className={`nh-tag ${tone}`}><Ic i={icon} s={13} /><span data-testid="count">{n}</span></span>
          <span className="tx"><b>{title}</b><span data-testid="sub">{sub}</span></span>
          <Ic i={ChevronRight} s={15} cls="chev" />
        </Link>
        {extra}
      </div>,
    );
  };
  const late = (qb?.data?.open_invoices ?? []).filter((i) => (i.days_late ?? 0) > 90);
  if (late.length) {
    const sum = late.reduce((a, i) => a + (i.balance ?? 0), 0);
    const oldest = late.reduce((a, i) => ((i.days_late ?? 0) > (a.days_late ?? 0) ? i : a));
    row("late-invoices", "/quickbooks", "crit", HandCoins, late.length, "Invoices over 90 days",
        `${usd(sum)} · oldest ${oldest.customer ?? "—"}, ${oldest.days_late} days`);
  }
  const quiet = fresh.data?.quiet ?? [];
  if (quiet.length) {
    row("feeds", quiet.length === 1 ? `/sources/${quiet[0].code}` : "/sources", "warn", ClockAlert, quiet.length,
        quiet.length === 1 ? `${quiet[0].name} has gone quiet` : `${quiet.length} feeds have gone quiet`,
        quiet.length === 1 ? quiet[0].summary : quiet.map((q) => q.name).join(" · "));
  }
  const i = d.issues;
  const newest = (errors.data ?? []).slice(0, 3);
  row("issues", "/data-quality", i.error ? "crit" : i.total ? "warn" : "good", i.error ? OctagonAlert : i.total ? TriangleAlert : CircleCheck,
      num(i.total), "Data issues to review", `${i.error} errors · ${i.warning} warnings`,
      i.error ? (
        <ul className="nh-errl" aria-label="Errors to look at" data-testid="errors">
          {errors.error ? <li><Ic i={CircleX} s={13} /><span>Could not load the errors: {errors.error}</span></li> : null}
          {!errors.data && !errors.error ? <li><span className="mut">Loading the newest errors…</span></li> : null}
          {newest.map((e) => (
            <li key={e.id}><Ic i={CircleX} s={13} /><span>{e.summary}<small>{e.source_name ?? e.source ?? "—"} · {dayShort(e.detected_at)}</small></span></li>
          ))}
          {errors.data ? <li><Link to="/data-quality">All {errors.data.length} errors →</Link></li> : null}
        </ul>
      ) : null);
  const chk = qb?.data?.invoice_check?.counts;
  if (chk) {
    const n = (chk.differs ?? 0) + (chk.sheet_only ?? 0) + (chk.books_only ?? 0);
    if (n) row("books", "/quickbooks", "warn", Scale, n, "Sheet and books disagree",
               `${chk.differs ?? 0} differ · ${chk.sheet_only ?? 0} only in the sheet · ${chk.books_only ?? 0} only in the books`);
  }
  if (d.tasks_open) {
    const next = tasks.data?.tasks.find((t) => !t.done);
    row("tasks", "/tasks", "neu", ListChecks, d.tasks_open, "Open tasks", next ? `Next: ${next.title}` : "On the Tasks page");
  }
  row("drafts", "/documents?status=draft", "neu", FilePenLine, num(d.documents.draft ?? 0), "Draft documents",
      `${num(d.documents.issued ?? 0)} issued, not yet shipped`);
  const caught = !late.length && !quiet.length && !i.total && !d.tasks_open && !(d.documents.draft ?? 0);
  return (
    <Card area="look" icon={ListChecks} title="Needs a look" isNew testId="needs-a-look">
      {caught ? <Clear title="All caught up">No late invoices, late feeds, data issues, open tasks or drafts. New items appear here after each read.</Clear> : null}
      <div>{rows}</div>
    </Card>
  );
}

// ───────────────────────────── sales by month ─────────────────────────────
interface Bar { label: string; ty: number | null; ly: number | null; star?: boolean; prior?: boolean }

function SalesChart({ period, years }: { period: Period; years: Loaded<Years> }) {
  const [table, setTable] = useState(false);
  const seg = (
    <span className="nh-seg" role="radiogroup" aria-label="Show as">
      <button type="button" role="radio" aria-checked={!table} onClick={() => setTable(false)}><Ic i={ChartColumn} s={15} />Chart</button>
      <button type="button" role="radio" aria-checked={table} onClick={() => setTable(true)}><Ic i={Table} s={15} />Table</button>
    </span>
  );
  if (years.error) return <Card area="chart" icon={TrendingUp} title="Sales by month" right={seg}><Failed what="sales by month" error={years.error} retry={years.reload} /></Card>;
  if (!years.data) return <Card area="chart" icon={TrendingUp} title="Sales by month" right={seg}><Skel n={5} /></Card>;
  const y = years.data;
  if (!y.available) return <Card area="chart" icon={TrendingUp} title="Sales by month"><Clear icon={TrendingUp} good={false} title="No sales workbook yet">Months appear once the gross sales workbook has been read.</Clear></Card>;

  let bars: Bar[]; let title: string; let y1: string; let y0: string; let note: ReactNode = null;
  if (period === "l12") {
    const t = y.ttm;
    bars = t.months.map((m) => ({ label: `${MON[m.month - 1]}${m.year < y.year ? "′" : ""}`, ty: m.usd, ly: m.last_usd, prior: m.year < y.year,
                                  star: y.partial_month && m.year === y.year && m.month === y.months_reported }));
    const a = t.months[0]; const b = t.months[11];
    y1 = `${MON[a.month - 1]} ${a.year} – ${MON[b.month - 1]} ${b.year}`; y0 = `${MON[a.month - 1]} ${a.year - 1} – ${MON[b.month - 1]} ${b.year - 1}`;
    title = t.change == null ? "Sales by month, last 12 months" : `Sales by month: ${t.change >= 0 ? "up" : "down"} ${Math.abs(t.change * 100).toFixed(1)}% on the 12 months before`;
    note = <>′ {y.last_year} months.</>;
  } else {
    bars = y.monthly.filter((m) => m.month <= y.months_reported).map((m) => ({
      label: MON[m.month - 1], ty: m.usd, ly: m.last_usd, star: y.partial_month && m.month === y.months_reported }));
    y1 = String(y.year); y0 = String(y.last_year);
    const c = y.ytd.change;
    title = c == null ? `Sales by month, ${y.year} (no ${y.last_year} sales to compare with)`
      : `Sales by month: ${c >= 0 ? "up" : "down"} ${Math.abs(c * 100).toFixed(1)}% on the same months of ${y.last_year}`;
  }
  const lastMon = MONTH[y.months_reported - 1];
  const star = y.partial_month ? <p className="nh-cnote">* {lastMon} so far: to {dayShort(y.as_of)} {y.year}, against all of {lastMon} {y.last_year} (the workbook has whole months only).</p> : null;
  const totalTy = bars.reduce((a, b) => a + (b.ty ?? 0), 0);
  const totalLy = bars.every((b) => b.ly == null) ? null : bars.reduce((a, b) => a + (b.ly ?? 0), 0);
  return (
    <Card area="chart" icon={TrendingUp} title={title} right={seg} testId="sales-chart">
      {table ? (
        <div className="nh-tw">
          <table className="nh-t" data-testid="chart-table">
            <thead><tr><th>Month</th><th className="r">{y1.length > 4 ? "This 12" : y1}</th><th className="r">{y0.length > 4 ? "The 12 before" : y0}</th><th className="r">Change</th></tr></thead>
            <tbody>
              {bars.map((b) => (
                <tr key={b.label}><td>{b.label}{b.star ? "*" : ""}</td><td className="r">{usd(b.ty)}</td><td className="r">{usd(b.ly)}</td>
                  <td className="r tg">{b.ty != null && b.ly ? (b.star ? <span className="mut">part month</span> : <Delta change={b.ty / b.ly - 1} />) : "—"}</td></tr>
              ))}
              <tr className="nh-total"><td>Total</td><td className="r">{usd(totalTy)}</td><td className="r">{usd(totalLy)}</td>
                <td className="r tg">{totalLy ? <Delta change={totalTy / totalLy - 1} /> : "—"}</td></tr>
            </tbody>
          </table>
        </div>
      ) : <Bars bars={bars} y1={y1} y0={y0} />}
      {!table ? (
        <div className="nh-legend"><span><i style={{ background: "var(--nx-chart-thisYear)" }} />{y1}</span><span><i style={{ background: "var(--nx-chart-lastYear)" }} />{y0}</span></div>
      ) : null}
      {star}
      {note ? <p className="nh-cnote">{note}</p> : null}
      <p className="nh-cnote">From the gross sales workbook ({y.file}), as of {day(y.as_of)}. <Link to="/sales?tab=years">Sales › Years →</Link></p>
    </Card>
  );
}

function niceTop(max: number) {
  if (max <= 0) return 1000;
  const raw = max / 4;
  const p = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * p).find((s) => s >= raw) ?? raw;
  return step * 4;
}
const kUsd = (v: number) => (v >= 1e6 ? `$${+(v / 1e6).toFixed(2)}M` : v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`);

function Bars({ bars, y1, y0 }: { bars: Bar[]; y1: string; y0: string }) {
  const top = niceTop(Math.max(...bars.flatMap((b) => [b.ty ?? 0, b.ly ?? 0])));
  const h = (v: number | null) => `${Math.max(0, ((v ?? 0) / top) * 100).toFixed(1)}%`;
  return (
    <>
      <div className="nh-chart" data-testid="chart">
        {[0, 25, 50, 75, 100].map((p) => <span key={p} className={`gl${p ? "" : " axis"}`} style={{ bottom: `${p}%` }} />)}
        {[0, 25, 50, 75, 100].map((p) => <span key={`y${p}`} className="yl" style={{ bottom: `${p}%` }}>{kUsd((top * p) / 100)}</span>)}
        {bars.map((b) => {
          const lab = `${b.label}${b.star ? " (part month)" : ""}: ${y1} ${usd(b.ty)}, ${y0} ${b.ly == null ? "no data" : usd(b.ly)}`;
          return (
            <div key={b.label} className="nh-grpb" role="img" aria-label={lab} tabIndex={0}>
              <i className="ty" style={{ height: h(b.ty) }} />
              {b.ly != null ? <i className="ly" style={{ height: h(b.ly) }} /> : null}
              <span className="tip">{lab}</span>
            </div>
          );
        })}
      </div>
      <div className="nh-months" aria-hidden="true">{bars.map((b) => <span key={b.label}>{b.label}{b.star ? "*" : ""}</span>)}</div>
    </>
  );
}

// ───────────────────────────── owed to us (books only) ─────────────────────────────
function Owed({ qb, admin }: { qb: Loaded<Books>; admin: boolean }) {
  const right = <Link to="/quickbooks">Books →</Link>;
  const title = "Owed to us, oldest first";
  if (qb.error) return <Card area="owed" icon={HandCoins} title={title} right={right} isNew><Failed what="money owed" error={qb.error} retry={qb.reload} /></Card>;
  if (!qb.data) return <Card area="owed" icon={HandCoins} title={title} right={right} isNew><Skel n={5} /></Card>;
  const d = qb.data;
  if (!d.connected) {
    return (
      <Card area="owed" icon={HandCoins} title={title} isNew testId="owed">
        <Clear icon={Scale} good={false} title="QuickBooks is not connected yet">
          {admin ? <>Connect it once on the <Link to="/quickbooks">Books page</Link>: sign in with Intuit and allow read-only access. Open invoices and days late appear here after the first read.</>
            : "An admin connects it on the Books page. Open invoices and days late appear here after the first read."}
        </Clear>
      </Card>
    );
  }
  if (!d.as_of) return <Card area="owed" icon={HandCoins} title={title} right={right} isNew testId="owed"><Clear icon={Scale} good={false} title="Not read yet">QuickBooks is connected; open invoices appear after its first read.</Clear></Card>;
  const open = d.open_invoices.filter((i) => (i.balance ?? 0) > 0);
  if (!open.length) return <Card area="owed" icon={HandCoins} title={title} right={right} isNew testId="owed"><Clear title="Nothing owed">Every invoice in QuickBooks is paid (read {when(d.as_of)}).</Clear></Card>;
  const lateTag = (days: number | null, long = false) => {
    if (!days) return <span className="nh-tag neu">{long ? "not late" : "—"}</span>;
    const crit = days > 90;
    return <span className={`nh-tag ${crit ? "crit" : "warn"}`}><Ic i={crit ? OctagonAlert : ClockAlert} s={13} />{days} d{long ? " late" : ""}</span>;
  };
  return (
    <Card area="owed" icon={HandCoins} title={title} right={right} isNew testId="owed">
      <div className="nh-tw">
        <table className="nh-t">
          <thead><tr><th>Customer · invoice</th><th className="r c-days shw">Days late</th><th className="r c-open">Open</th></tr></thead>
          <tbody>{open.slice(0, 5).map((i, k) => (
            <tr key={`${i.doc_number}-${k}`}>
              <td className="two"><Link className="trunc" to="/quickbooks">{i.customer ?? "—"}</Link>
                <span className="sub"><span className="mono">{i.doc_number ?? "—"}</span> · due {dayShort(i.due_date)}</span></td>
              <td className="r tg shw">{lateTag(i.days_late)}</td>
              <td className="r tg">{i.currency === "USD" ? usd(i.balance) : `${num(i.balance, 2)} ${i.currency}`}<span className="shn"><br />{lateTag(i.days_late, true)}</span></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <div className="nh-foot"><span>{usd(d.open_total ?? 0)} open on {d.open_invoices.length}{d.open_invoices.length >= 200 ? "+" : ""} invoices · from QuickBooks, read {when(d.as_of)}</span>
        <Link to="/quickbooks">All open invoices →</Link></div>
    </Card>
  );
}

// ───────────────────────────── top customers + channels (the sales report) ─────────────────────────────
function TopCustomers({ customers }: { customers: Loaded<{ customers: Cust[] }> }) {
  const right = <Link to="/customers">All customers →</Link>;
  if (customers.error) return <Card area="cust" icon={Users} title="Top customers" right={right}><Failed what="top customers" error={customers.error} retry={customers.reload} /></Card>;
  if (!customers.data) return <Card area="cust" icon={Users} title="Top customers" right={right}><Skel n={6} /></Card>;
  const rows = customers.data.customers;
  if (!rows.length) return <Card area="cust" icon={Users} title="Top customers" right={right}><Clear icon={Users} good={false} title="No customer sales yet">Customers appear once the sales report has invoices.</Clear></Card>;
  const top = rows[0];
  const share = (c: Cust) => (c.share > 0.3 ? <span className="nh-tag warn"><Ic i={TriangleAlert} s={13} />{pct(c.share)}</span> : <>{pct(c.share)}</>);
  return (
    <Card area="cust" icon={Users} title="Top customers" right={right} testId="top-customers">
      <div className="nh-tw">
        <table className="nh-t">
          <thead><tr><th>Customer</th><th className="r c-sales">Sales</th><th className="r c-share shw">Share</th></tr></thead>
          <tbody>{rows.map((c) => (
            <tr key={c.id} data-testid="customer-row">
              <td className="two"><Link className="trunc" to={`/customers/${c.id}`}>{c.name}</Link><span className="nh-bar" style={{ width: `${(c.amount_usd / top.amount_usd) * 100}%` }} /></td>
              <td className="r tg">{usd(c.amount_usd)}<span className="shn"><br /><span className="mut">{share(c)}</span></span></td>
              <td className="r tg shw" data-testid="share">{share(c)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <div className="nh-foot"><span data-testid="largest">{top.share > 0.3 ? <><Ic i={TriangleAlert} s={13} /> Largest customer is {pct(top.share)} of sales ({top.name}), over the 30% warn level</>
        : <>Largest customer is {pct(top.share)} of sales ({top.name})</>} · all invoices in the sales report; the period above does not change it</span></div>
    </Card>
  );
}

function Channels({ o, monthly, years }: { o: Loaded<Overview>; monthly: Loaded<Monthly>; years: Years | null }) {
  const right = <Link to="/sales">All sales →</Link>;
  if (o.error) return <Card area="chan" icon={TrendingUp} title="Sales by channel" right={right}><Failed what="sales by channel" error={o.error} retry={o.reload} /></Card>;
  if (!o.data) return <Card area="chan" icon={TrendingUp} title="Sales by channel" right={right}><Skel n={4} /></Card>;
  const s = o.data.sales;
  const top = Math.max(1, ...s.by_channel.map((c) => c.amount_usd));
  const und = monthly.data?.undated;
  return (
    <Card area="chan" icon={TrendingUp} title="Sales by channel" right={right} testId="channels">
      {s.reconciles === false ? (
        <div className="nh-banner crit" role="alert" style={{ marginBottom: 10 }}><Ic i={OctagonAlert} />
          <div><b>The sales report's own total does not add up</b>Its lines and its total differ; the figures below are the lines. <Link to="/data-quality">See Issues</Link></div></div>
      ) : null}
      <div className="nh-tw">
        <table className="nh-t">
          <thead><tr><th>Channel</th><th className="r c-chs">Sales</th><th className="r cs c-cases">Cases</th></tr></thead>
          <tbody>
            {s.by_channel.map((c) => (
              <tr key={c.code}>
                <td className="two"><Link className="trunc" to={`/sales?tab=invoices&channel=${c.code}`} title={c.name}>{c.name}</Link><span className="nh-bar" style={{ width: `${(c.amount_usd / top) * 100}%` }} /></td>
                <td className="r two">{usd(c.amount_usd)}<span className="sub">{num(c.cases)} cs</span></td>
                <td className="r cs">{num(c.cases)}</td>
              </tr>
            ))}
            <tr className="nh-total" data-testid="channels-total">
              <td>All channels</td>
              <td className="r two"><span data-testid="value">{usd(s.amount_usd)}</span><span className="sub" data-testid="sub">{num(s.cases)} cases, all channels</span></td>
              <td className="r cs">{num(s.cases)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="nh-foot"><span>Year to date, from the sales report as of {day(s.as_of)}; the period above does not change it.
        {und && und.count > 0 ? ` ${und.count} undated invoices (${usd(und.amount_usd)}) are in these totals but in no month.` : ""}
        {years ? ` The Sales tile reads the gross sales workbook (as of ${day(years.as_of)}), so the two totals can differ: ` : ""}
        {years ? <Link to="/sales?tab=years" style={{ marginLeft: 0 }}>Sales › Years compares them →</Link> : null}</span></div>
    </Card>
  );
}

// ───────────────────────────── recent documents ─────────────────────────────
function RecentDocs({ docs }: { docs: Loaded<DocSummary[]> }) {
  const right = <Link to="/documents">All documents →</Link>;
  if (docs.error) return <Card area="docs" icon={FileText} title="Recent documents" right={right}><Failed what="recent documents" error={docs.error} retry={docs.reload} /></Card>;
  if (!docs.data) return <Card area="docs" icon={FileText} title="Recent documents" right={right}><Skel n={6} /></Card>;
  if (!docs.data.length) {
    return <Card area="docs" icon={FileText} title="Recent documents" right={right}>
      <Clear icon={FileText} good={false} title="No documents yet">Upload an invoice, PO or transfer on the <Link to="/documents">Documents page</Link>, or <Link to="/documents/new">make one</Link>. Uploads never change stock.</Clear>
    </Card>;
  }
  return (
    <Card area="docs" icon={FileText} title="Recent documents" right={right} testId="recent-docs">
      <div className="nh-tw">
        <table className="nh-t">
          <thead><tr><th>Document</th><th className="cpw">Counterparty</th><th className="c-status stw">Status</th><th className="r c-total">Total</th></tr></thead>
          <tbody>{docs.data.slice(0, 6).map((d) => (
            <tr key={d.id} data-testid="doc-row">
              <td className="two"><Link className="trunc" to={`/documents/${d.id}`}>{d.type_label} <span className="mono">{d.number}</span></Link>
                <span className="sub trunc cpn">{d.counterparty ?? "—"}<span className="stn"> · {DOC_STATUS[d.status].word}</span></span></td>
              <td className="cpw">{d.counterparty ?? "—"}</td>
              <td className="tg stw"><DocTag d={d} /></td>
              <td className="r">{usd(d.total_amount)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <div className="nh-foot"><span>Last 6, newest first</span></div>
    </Card>
  );
}

// ───────────────────────────── dialogs: a DRAFT through the documents API ─────────────────────────────
interface Saved { kind: "transfer" | "po"; id: number; number: string; title: string; sub: string; rowKey: string; note: string }

/** Focus goes into the dialog when it opens and back where it was when it closes; Escape closes it. */
function useDialogBasics(onClose: () => void) {
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    box.current?.querySelector<HTMLElement>("input, select")?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") close.current(); };
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("keydown", esc); prev?.focus?.(); };
  }, []);
  return box;
}

/** Not saved: the server's reason in plain words (409 = the number is taken). */
function Refusal({ err, label, onUseNext }: { err: { status: number; message: string; next?: string } | null; label: string; onUseNext: (n: string) => void }) {
  if (!err) return null;
  return (
    <div className="nh-banner crit" role="alert" data-testid="refusal">
      <Ic i={CircleX} />
      <div>
        <b>Not saved: {err.message.charAt(0).toLowerCase() + err.message.slice(1)}</b>
        {err.status === 409 ? <>Someone saved a {label} with that number. {err.next ? <>Change the number to {err.next} and save again; everything else is kept. <button type="button" className="linkish" onClick={() => onUseNext(err.next!)}>Use {err.next}</button></> : "Change the number and save again; everything else is kept."}</>
          : <>Nothing was saved. Fix what it says and save again, or open the full form.</>}
      </div>
    </div>
  );
}

function Problems({ list, onPick }: { list: { field: string; text: string }[]; onPick: (f: string) => void }) {
  if (!list.length) return null;
  return (
    <div className="nh-banner crit" role="alert" data-testid="problems">
      <Ic i={CircleX} />
      <div><b>{list.length} thing{list.length === 1 ? "" : "s"} to fix before saving</b>
        {list.map((p) => <div key={p.field}><button type="button" className="linkish" onClick={() => onPick(p.field)}>{p.text}</button></div>)}</div>
    </div>
  );
}

function Field({ id, label, help, error, children }: { id: string; label: string; help?: ReactNode; error?: string; children: ReactNode }) {
  return (
    <div className="nh-fld">
      <label htmlFor={id}>{label}</label>
      {children}
      {error ? <span className="nh-emsg" id={`${id}-err`}><Ic i={CircleAlert} s={13} />{error}</span> : help ? <p className="nh-help" id={`${id}-help`}>{help}</p> : null}
    </div>
  );
}

function Dialog({ title, onClose, foot, children, boxRef }: { title: string; onClose: () => void; foot: ReactNode; children: ReactNode; boxRef: RefObject<HTMLDivElement> }) {
  return (
    <div className="nh-ov" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="nh-dlg" role="dialog" aria-modal="true" aria-label={title} ref={boxRef}>
        <div className="nh-dlg-h"><h2>{title}</h2><button type="button" className="nh-btn qt io" aria-label="Close" onClick={onClose}><Ic i={X} s={15} /></button></div>
        <div className="nh-dlg-b">{children}</div>
        <div className="nh-dlg-f">{foot}</div>
      </div>
    </div>
  );
}

function TransferDialog({ row, rows, warn, onClose, onSaved }: { row: CoverRow; rows: CoverRow[]; warn: number; onClose: () => void; onSaved: (s: Saved) => void }) {
  const nav = useNavigate();
  const box = useDialogBasics(onClose);
  const defaults = useApi<DocDefaults>("/documents/new?doc_type=transfer_invoice");
  const locs = useApi<Loc[]>("/locations");
  const items = useApi<Item[]>("/items");
  // Where to send it from: the warehouse holding the most of this product (the same readings as the Stock page).
  const holders = useMemo(() => rows.filter((r) => r.sku === row.sku && r.location !== row.location && r.cases > 0).sort((a, b) => b.cases - a.cases), [rows, row]);
  const need = Math.max(1, Math.ceil(warn * (row.weekly ?? 0) - row.cases));
  // Never more than the sending warehouse holds: the suggestion has to be one that can be saved.
  const most = holders[0] ? Math.floor(holders[0].cases) : null;
  const suggested = most != null && most >= 1 ? Math.min(need, most) : need;
  const [f, setF] = useState({ number: "", ship_date: "", from: holders[0]?.location ?? "", to: row.location, cases: String(suggested) });
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<{ status: number; message: string; next?: string } | null>(null);
  const [tried, setTried] = useState(false);
  useEffect(() => { if (defaults.data) setF((v) => (v.number ? v : { ...v, number: defaults.data!.number })); }, [defaults.data]);

  const from = rows.find((r) => r.sku === row.sku && r.location === f.from);
  const toName = locs.data?.find((l) => l.code === f.to)?.name ?? row.location_name;
  const fromName = locs.data?.find((l) => l.code === f.from)?.name ?? f.from;
  const n = Number(f.cases);
  const problems: { field: string; text: string }[] = [];
  if (!f.number.trim()) problems.push({ field: "t-number", text: "Number: empty" });
  if (!f.from) problems.push({ field: "t-from", text: "From: choose where it ships from" });
  else if (f.from === f.to) problems.push({ field: "t-from", text: "From and To are the same warehouse" });
  if (!(n > 0) || !Number.isFinite(n)) problems.push({ field: "t-cases", text: "Cases: enter how many, more than 0" });
  else if (from && n > from.cases) problems.push({ field: "t-cases", text: `Cases: more than ${from.location_name} has (${num(from.cases)})` });
  const shown = tried ? problems : [];
  const err = (id: string) => shown.find((p) => p.field === id)?.text;
  const item = items.data?.find((i) => i.sku === row.sku);

  const body = (): Record<string, unknown> => {
    const d = { ...(defaults.data ?? {}) } as Record<string, unknown>;
    // The usual ship-to and consignee belong to the usual destination; for another warehouse they are left for the full form.
    if (defaults.data && f.to !== defaults.data.to_location) { d.ship_to_id = null; d.consignee_id = null; }
    return { ...d, doc_type: "transfer_invoice", number: f.number.trim(), ship_date: f.ship_date || null, from_location: f.from, to_location: f.to,
             lines: [{ sku: row.sku, description: item ? describe(item) : row.product, qty: n, uom: "cases", unit_price: 0 }] };
  };
  const save = async () => {
    setTried(true); setRefused(null);
    if (problems.length) { document.getElementById(problems[0].field)?.focus(); return; }
    setBusy(true);
    try {
      const doc = await api.post<{ id: number; number: string }>("/documents", body());
      onSaved({ kind: "transfer", id: doc.id, number: doc.number, rowKey: `t:${row.location}:${row.sku}`,
                title: `Transfer invoice ${doc.number} saved as a draft`,
                sub: `${num(n)} cases of ${row.product}, ${fromName} → ${toName}. Stock changes only when it is marked shipped.`,
                note: `Draft transfer ${doc.number} · ${num(n)} cases, not shipped yet` });
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0;
      let next: string | undefined;
      if (status === 409) next = (await api.get<DocDefaults>("/documents/new?doc_type=transfer_invoice").catch(() => null))?.number;
      setRefused({ status, message: (e as Error).message, next });
    } finally { setBusy(false); }
  };
  const full = () => {
    const prefill: Partial<Form> = { number: f.number, ship_date: f.ship_date, from_location: f.from, to_location: f.to,
      ...(defaults.data && f.to !== defaults.data.to_location ? { ship_to_id: "", consignee_id: "" } : {}),
      lines: [{ sku: row.sku, description: item ? describe(item) : row.product, qty: String(n || ""), uom: "cases", unit_price: "" }] };
    nav("/documents/new?type=transfer_invoice", { state: { prefill } });
  };
  const ready = Boolean(defaults.data && locs.data && items.data);
  return (
    <Dialog title="Make transfer invoice" onClose={onClose} boxRef={box} foot={<>
      <button type="button" className="nh-btn qt left" onClick={full} disabled={busy}>Open the full form</button>
      <button type="button" className="nh-btn qt" onClick={onClose} disabled={busy}>Cancel</button>
      <button type="button" className={`nh-btn pri${busy ? " busy" : ""}`} onClick={() => void save()} disabled={busy || !ready}>
        {busy ? <><Ic i={LoaderCircle} s={15} cls="spin" />Saving…</> : <><Ic i={ArrowRightLeft} s={15} />Save draft</>}</button>
    </>}>
      {refused ? <Refusal err={refused} label="transfer invoice" onUseNext={(x) => { setF((v) => ({ ...v, number: x })); setRefused(null); }} /> : <Problems list={shown} onPick={(id) => document.getElementById(id)?.focus()} />}
      {!refused && !shown.length ? (
        <div className="nh-banner info"><Ic i={Info} /><div><b>Prefilled from Running low</b>{row.product} has {num(row.weeks_left, 1)} weeks left at {row.location_name} ({num(row.cases)} cases, {num(row.weekly, 1)} out a week). Check each field; nothing is saved until you press Save draft.</div></div>
      ) : null}
      {defaults.error || locs.error || items.error ? <div className="nh-banner crit" role="alert"><Ic i={CircleX} /><div><b>Could not load the form</b>{defaults.error || locs.error || items.error}</div></div> : null}
      <div className="nh-cols2">
        <Field id="t-number" label="Number" help="Next free transfer number" error={err("t-number") || (refused?.status === 409 ? refused.message : undefined)}>
          <input id="t-number" value={f.number} disabled={busy} aria-invalid={Boolean(err("t-number") || refused?.status === 409)} onChange={(e) => setF({ ...f, number: e.target.value })} />
        </Field>
        <Field id="t-ship" label="Ship date" help="Optional for a draft">
          <input id="t-ship" type="date" value={f.ship_date} disabled={busy} onChange={(e) => setF({ ...f, ship_date: e.target.value })} />
        </Field>
      </div>
      <div className="nh-cols2">
        <Field id="t-from" label="From" error={err("t-from")} help={holders[0] ? `Holds the most: ${num(holders[0].cases)} cases` : "No other warehouse reports this product"}>
          <select id="t-from" value={f.from} disabled={busy} aria-invalid={Boolean(err("t-from"))} onChange={(e) => setF({ ...f, from: e.target.value })}>
            <option value="">Choose…</option>
            {(locs.data ?? []).map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
        </Field>
        <Field id="t-to" label="To" help="The warehouse running low">
          <select id="t-to" value={f.to} disabled={busy} onChange={(e) => setF({ ...f, to: e.target.value })}>
            {(locs.data ?? [{ code: row.location, name: row.location_name }]).map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
        </Field>
      </div>
      <div className="nh-fld"><span className="lbl">Product</span><span className="ro" data-testid="t-product">{row.product} · {row.sku}</span></div>
      <Field id="t-cases" label="Cases" error={err("t-cases")}
             help={<><b>{num(need)}</b> brings {row.location_name} to {num(warn)} weeks: {num(warn)} × {num(row.weekly, 1)} − {num(row.cases)}
               {suggested < need && holders[0] ? <>; {holders[0].location_name} holds {num(most)}, so {num(suggested)} is suggested</> : null}</>}>
        <input id="t-cases" inputMode="numeric" value={f.cases} disabled={busy} aria-invalid={Boolean(err("t-cases"))} onChange={(e) => setF({ ...f, cases: e.target.value })} />
      </Field>
      <p className="nh-help">Saved as a draft transfer invoice. A draft never changes stock; once it is marked shipped, the cases show as in transit to {toName}. The price per case is left for the full form.</p>
    </Dialog>
  );
}

function PODialog({ issue, onClose, onSaved }: { issue: Issue; onClose: () => void; onSaved: (s: Saved) => void }) {
  const nav = useNavigate();
  const box = useDialogBasics(onClose);
  const fx = rawFacts(issue);
  const defaults = useApi<DocDefaults>("/documents/new?doc_type=purchase_order");
  const vendors = useApi<Party[]>("/parties?kind=vendor");
  const parties = useApi<Party[]>("/parties");
  const [f, setF] = useState({ number: "", required: "", vendor: "", qty: String(fx.short || ""), price: "" });
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<{ status: number; message: string; next?: string } | null>(null);
  const [tried, setTried] = useState(false);
  useEffect(() => { if (defaults.data) setF((v) => (v.number ? v : { ...v, number: defaults.data!.number })); }, [defaults.data]);
  const shipTo = parties.data?.find((p) => p.id === defaults.data?.ship_to_id)?.name;
  const qty = Number(f.qty); const price = f.price === "" ? 0 : Number(f.price);
  const problems: { field: string; text: string }[] = [];
  if (!f.number.trim()) problems.push({ field: "p-number", text: "Number: empty" });
  if (!(qty > 0) || !Number.isFinite(qty)) problems.push({ field: "p-qty", text: `Quantity: enter how many to order. The shortage is ${num(fx.short)} ${fx.uom}.` });
  if (!(price >= 0) || !Number.isFinite(price)) problems.push({ field: "p-price", text: "Unit price: a number, 0 or more" });
  const shown = tried ? problems : [];
  const err = (id: string) => shown.find((p) => p.field === id)?.text;
  const total = qty > 0 && f.price !== "" && price >= 0 ? Math.round(qty * price * 100) / 100 : null;   // no price typed: no total claimed
  const vendorName = vendors.data?.find((v) => String(v.id) === f.vendor)?.name;

  const save = async () => {
    setTried(true); setRefused(null);
    if (problems.length) { document.getElementById(problems[0].field)?.focus(); return; }
    setBusy(true);
    try {
      const doc = await api.post<{ id: number; number: string }>("/documents", {
        ...(defaults.data ?? {}), doc_type: "purchase_order", number: f.number.trim(), required_date: f.required || null,
        vendor_id: f.vendor ? Number(f.vendor) : null,
        lines: [{ sku: fx.sku, description: fx.name, qty, uom: fx.uom || "units", unit_price: price }],
      });
      onSaved({ kind: "po", id: doc.id, number: doc.number, rowKey: `raw:${issue.id}`,
                title: `Purchase order ${doc.number} saved as a draft`,
                sub: `${num(qty)} ${fx.uom} of ${fx.name}${vendorName ? ` from ${vendorName}` : ""}${total ? `, ${usd(total)}` : ""}. Issue it to send it.`,
                note: `Draft PO ${doc.number} · ${num(qty)} ${fx.uom}, not ordered yet` });
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0;
      let next: string | undefined;
      if (status === 409) next = (await api.get<DocDefaults>("/documents/new?doc_type=purchase_order").catch(() => null))?.number;
      setRefused({ status, message: (e as Error).message, next });
    } finally { setBusy(false); }
  };
  const full = () => {
    const prefill: Partial<Form> = { number: f.number, required_date: f.required, vendor_id: f.vendor,
      lines: [{ sku: fx.sku, description: fx.name, qty: f.qty, uom: fx.uom || "units", unit_price: f.price }] };
    nav("/documents/new?type=purchase_order", { state: { prefill } });
  };
  const ready = Boolean(defaults.data && vendors.data);
  return (
    <Dialog title="Raise purchase order" onClose={onClose} boxRef={box} foot={<>
      <button type="button" className="nh-btn qt left" onClick={full} disabled={busy}>Open the full form</button>
      <button type="button" className="nh-btn qt" onClick={onClose} disabled={busy}>Cancel</button>
      <button type="button" className={`nh-btn pri${busy ? " busy" : ""}`} onClick={() => void save()} disabled={busy || !ready}>
        {busy ? <><Ic i={LoaderCircle} s={15} cls="spin" />Saving…</> : <><Ic i={ClipboardList} s={15} />Save draft</>}</button>
    </>}>
      {refused ? <Refusal err={refused} label="purchase order" onUseNext={(x) => { setF((v) => ({ ...v, number: x })); setRefused(null); }} /> : <Problems list={shown} onPick={(id) => document.getElementById(id)?.focus()} />}
      {!refused && !shown.length ? (
        <div className="nh-banner info"><Ic i={Info} /><div><b>Prefilled from Running low</b>BCI's production plan is short {num(fx.short)} {fx.uom} of {fx.name} after the {num(fx.have)} they hold{fx.needed ? `, first needed in ${fx.needed}` : ""}. Check each field; nothing is saved until you press Save draft.</div></div>
      ) : null}
      {defaults.error || vendors.error ? <div className="nh-banner crit" role="alert"><Ic i={CircleX} /><div><b>Could not load the form</b>{defaults.error || vendors.error}</div></div> : null}
      <div className="nh-cols2">
        <Field id="p-number" label="Number" help="Next free PO number" error={err("p-number") || (refused?.status === 409 ? refused.message : undefined)}>
          <input id="p-number" value={f.number} disabled={busy} aria-invalid={Boolean(err("p-number") || refused?.status === 409)} onChange={(e) => setF({ ...f, number: e.target.value })} />
        </Field>
        <Field id="p-required" label="Required ship date" help={fx.needed ? `Needed in ${fx.needed}; optional for a draft` : "Optional for a draft"}>
          <input id="p-required" type="date" value={f.required} disabled={busy} onChange={(e) => setF({ ...f, required: e.target.value })} />
        </Field>
      </div>
      <div className="nh-cols2">
        <Field id="p-vendor" label="Vendor" help="No supplier is on record for this item; optional for a draft">
          <select id="p-vendor" value={f.vendor} disabled={busy} onChange={(e) => setF({ ...f, vendor: e.target.value })}>
            <option value="">Not chosen yet</option>
            {(vendors.data ?? []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </Field>
        <div className="nh-fld"><span className="lbl">Deliver to</span><span className="ro">{shipTo ?? "—"}</span><p className="nh-help">The usual ship-to for a PO (Settings)</p></div>
      </div>
      <div className="nh-fld"><span className="lbl">Item</span><span className="ro" data-testid="p-item">{fx.name} · {fx.sku}</span></div>
      <div className="nh-cols2">
        <Field id="p-qty" label={`Quantity (${fx.uom || "units"})`} error={err("p-qty")} help={<><b>{num(fx.short)}</b> = the shortage</>}>
          <input id="p-qty" inputMode="decimal" value={f.qty} disabled={busy} aria-invalid={Boolean(err("p-qty"))} onChange={(e) => setF({ ...f, qty: e.target.value })} />
        </Field>
        <Field id="p-price" label="Unit price (USD)" error={err("p-price")} help="No price on record; optional for a draft">
          <input id="p-price" inputMode="decimal" value={f.price} disabled={busy} aria-invalid={Boolean(err("p-price"))} onChange={(e) => setF({ ...f, price: e.target.value })} />
        </Field>
      </div>
      <div className="nh-tot"><span>Total (computed)</span><span className="num">{total == null ? "—" : usd(total)}</span></div>
      <p className="nh-help">Saved as a draft purchase order: nothing is sent and stock never changes. Issue it from the document page when it is ready.</p>
    </Dialog>
  );
}

function Toast({ saved, onClose, onUndone }: { saved: Saved; onClose: () => void; onUndone: (key: string) => void }) {
  const [state, setState] = useState<"saved" | "undoing" | "undone" | string>("saved");
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => { const t = window.setTimeout(() => close.current(), 12000); return () => window.clearTimeout(t); }, [state]);
  const undo = async () => {
    setState("undoing");
    try { await api.del(`/documents/${saved.id}`); onUndone(saved.rowKey); setState("undone"); }
    catch (e) { setState(`Not removed: ${(e as Error).message}`); }
  };
  return (
    <div className="nh-toast" role="status" data-testid="saved-toast">
      <span className={state === "saved" || state === "undone" ? "ico-good" : "ico-crit"}><Ic i={state === "saved" || state === "undone" || state === "undoing" ? CircleCheck : CircleX} /></span>
      <div className="t">
        {state === "undone" ? <><b>Draft {saved.number} removed</b><span>Nothing else changed.</span></>
          : <><b>{saved.title}</b><span>{state.startsWith("Not removed") ? state : saved.sub}</span>
            <div className="acts"><Link to={`/documents/${saved.id}`}>Open</Link>
              <button type="button" onClick={() => void undo()} disabled={state === "undoing"}>{state === "undoing" ? "Undoing…" : "Undo"}</button></div></>}
      </div>
      <button type="button" className="nh-btn qt io sm" aria-label="Dismiss" onClick={onClose}><Ic i={X} s={15} /></button>
    </div>
  );
}
